import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Divider, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PrintIcon from '@mui/icons-material/Print';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import { generarTiquetePDF } from '../../../historial/application/utils/pdfGenerator';
import { useSession } from '../../../../core/context/SessionContext';
import { useAuth } from 'react-oidc-context';
import { VentaApiService } from '../../infrastructure/api/VentaApiService';

interface EtiquetaPDF {
  guia: string;
  pdfBase64: string;
}

interface VentaConfirmacion {
  compraId: number;
  idPedidoSAP?: string | null;
  codigosRemision?: string[];
  estadoCompra?: string;
  pedidos?: unknown;
  cliente: { nombre: string; tipoDocumento: string; documento: string };
  metodoPago: string;
  total: number;
  tieneEnvioNacional?: boolean;
  ivaEnvios?: number;
  ivaEmpaques?: number;
  baseGravable?: number;
  totalImpuestos?: number;
}

type Moneda = 'COP' | 'MXN';

function formatCurrency(amount: number, moneda: Moneda): string {
  return new Intl.NumberFormat(moneda === 'COP' ? 'es-CO' : 'es-MX', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: moneda === 'COP' ? 0 : 2
  }).format(amount);
}

function guiasDeVenta(venta: { codigosRemision?: unknown } | null | undefined): string[] {
  const guias = venta?.codigosRemision;
  return Array.isArray(guias) ? guias as string[] : [];
}

function textoPedido(valor: unknown): string | null {
  if (typeof valor === 'string' && valor.length > 0) {
    return valor;
  }
  if (typeof valor === 'number') {
    return String(valor);
  }
  return null;
}

function identificadoresDePedido(venta: { idPedidoSAP?: unknown; codigosRemision?: unknown } | null | undefined): string[] {
  if (!venta) {
    return [];
  }
  const sap = textoPedido(venta.idPedidoSAP);
  return [
    ...(sap ? [sap] : []),
    ...guiasDeVenta(venta),
  ];
}

function mensajeVentaParcial(pedidos: unknown): string {
  const pedidoFallido = Array.isArray(pedidos)
    ? pedidos.find((p: { estado?: string; tipo?: string }) => p.estado === 'FALLIDO')
    : undefined;
  if (pedidoFallido?.tipo === 'envio') {
    return 'El envío nacional no se pudo procesar, pero el empaque se facturó correctamente.';
  }
  if (pedidoFallido?.tipo === 'empaque') {
    return 'El empaque no se pudo facturar, pero el envío nacional se procesó correctamente.';
  }
  return 'Una parte de la venta no se pudo procesar. Revisa el historial para más detalle.';
}

const DesgloseIvaMx: React.FC<{ venta: VentaConfirmacion; moneda: Moneda }> = ({ venta, moneda }) => {
  if (venta.ivaEnvios === undefined) {
    return null;
  }
  const ivaEnvios = venta.ivaEnvios ?? 0;
  const ivaEmpaques = venta.ivaEmpaques ?? 0;
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Base gravable</Typography>
        <Typography variant="body2">{formatCurrency(venta.baseGravable ?? 0, moneda)}</Typography>
      </Box>
      {ivaEnvios > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">IVA Envíos (16%)</Typography>
          <Typography variant="body2">{formatCurrency(ivaEnvios, moneda)}</Typography>
        </Box>
      )}
      {ivaEmpaques > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">IVA Empaques (16%)</Typography>
          <Typography variant="body2">{formatCurrency(ivaEmpaques, moneda)}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Total Impuestos</Typography>
        <Typography variant="body2">{formatCurrency(venta.totalImpuestos ?? 0, moneda)}</Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
    </>
  );
};

const DialogRotulos: React.FC<{
  open: boolean;
  etiquetasPDF: EtiquetaPDF[];
  onClose: () => void;
}> = ({ open, etiquetasPDF, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
      Rótulos de Envío ({etiquetasPDF.length})
    </DialogTitle>
    <DialogContent sx={{ p: 3 }}>
      {etiquetasPDF.map((etiqueta, i) => (
        <Box key={etiqueta.guia} sx={{ mb: i < etiquetasPDF.length - 1 ? 3 : 0 }}>
          {etiquetasPDF.length > 1 && (
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Guía {etiqueta.guia}
            </Typography>
          )}
          <Box
            component="embed"
            src={`data:application/pdf;base64,${etiqueta.pdfBase64}`}
            type="application/pdf"
            sx={{ width: '100%', height: '500px', border: '1px solid #e0e0e0', borderRadius: 1 }}
          />
        </Box>
      ))}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} variant="contained">
        Cerrar
      </Button>
    </DialogActions>
  </Dialog>
);

const AccionesConfirmacion: React.FC<{
  isImprimiendo: boolean;
  isImprimiendoRotulo: boolean;
  tieneEnvioNacional: boolean;
  onImprimirTiquete: () => void;
  onImprimirRotulo: () => void;
  onNuevaVenta: () => void;
  onHistorial: () => void;
}> = ({
  isImprimiendo,
  isImprimiendoRotulo,
  tieneEnvioNacional,
  onImprimirTiquete,
  onImprimirRotulo,
  onNuevaVenta,
  onHistorial,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Button
      variant="contained"
      color="secondary"
      size="large"
      startIcon={isImprimiendo ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
      onClick={onImprimirTiquete}
      disabled={isImprimiendo}
      sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 8 }}
    >
      {isImprimiendo ? 'Generando...' : 'Imprimir Tiquete PDF'}
    </Button>
    {tieneEnvioNacional && (
      <Button
        variant="outlined"
        color="secondary"
        size="large"
        startIcon={isImprimiendoRotulo ? <CircularProgress size={20} color="inherit" /> : <LocalOfferIcon />}
        onClick={onImprimirRotulo}
        disabled={isImprimiendoRotulo}
        sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 8 }}
      >
        {isImprimiendoRotulo ? 'Generando...' : 'Imprimir Rótulo'}
      </Button>
    )}
    <Button
      variant="outlined"
      color="primary"
      size="large"
      startIcon={<AddShoppingCartIcon />}
      onClick={onNuevaVenta}
      sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 8 }}
    >
      Realizar Nueva Venta
    </Button>
    <Button
      variant="text"
      color="inherit"
      startIcon={<HistoryIcon />}
      onClick={onHistorial}
      sx={{ mt: 0.5 }}
    >
      Ver Historial de Ventas
    </Button>
  </Box>
);

function etiquetaIdentificadorPedido(ids: string[], compraId?: number): { titulo: string; valor: string } {
  if (ids.length > 0) {
    return { titulo: 'Pedido SAP/Guía', valor: ids.join(', ') };
  }
  return { titulo: 'N° de Compra', valor: String(compraId ?? '—') };
}

function mensajeDeError(error: unknown, prefijo: string): string {
  const detail = error instanceof Error ? error.message : 'Error desconocido';
  return `${prefijo}: ${detail}`;
}

async function cargarYGenerarTiquete(
  venta: { compraId: number; ivaEnvios?: number; baseGravable?: number; ivaEmpaques?: number; totalImpuestos?: number },
  accessToken: string,
  terminal: { pais?: string; codigo_terminal?: number } | null | undefined,
  moneda: Moneda,
): Promise<void> {
  const datosTiquete = await VentaApiService.obtenerTiqueteBD(
    venta.compraId,
    accessToken,
    terminal?.pais ?? '',
    terminal?.codigo_terminal,
  );
  const isMX = terminal?.pais === 'MX';
  const backendProvidesBreakdown = isMX && datosTiquete?.ivaEnvios !== undefined;
  generarTiquetePDF({
    ...datosTiquete,
    moneda,
    ...(isMX && !backendProvidesBreakdown && venta.ivaEnvios !== undefined && {
      baseGravable: venta.baseGravable,
      ivaEnvios: venta.ivaEnvios,
      ivaEmpaques: venta.ivaEmpaques,
      totalImpuestos: venta.totalImpuestos,
    }),
  });
}

async function cargarEtiquetasRotulo(
  compraId: number,
  accessToken: string,
  terminal: { pais?: string; codigo_terminal?: number } | null | undefined,
): Promise<EtiquetaPDF[] | string> {
  const tiquete = await VentaApiService.obtenerTiqueteBD(
    compraId,
    accessToken,
    terminal?.pais ?? '',
    terminal?.codigo_terminal,
  );
  const guiasRotulo: string[] = Array.isArray(tiquete.guias) ? tiquete.guias : [];
  if (guiasRotulo.length === 0) {
    return 'No se encontraron guías asociadas a esta venta.';
  }
  const etiquetas = await VentaApiService.imprimirEtiquetas(guiasRotulo, accessToken, terminal?.pais ?? '');
  return etiquetas.map((e, idx) => ({ guia: guiasRotulo[idx], pdfBase64: e.pdfBase64 }));
}

const SinVentaReciente: React.FC<{ onVolver: () => void }> = ({ onVolver }) => (
  <Box sx={{ p: 3, flexGrow: 1, width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No hay datos de venta recientes.
    </Typography>
    <Button
      variant="contained"
      onClick={onVolver}
      sx={{ mt: 2 }}
      startIcon={<AddShoppingCartIcon />}
    >
      Volver al POS
    </Button>
  </Box>
);

export const ConfirmacionVentaPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const venta = location.state?.venta;
  if (!venta) {
    return <SinVentaReciente onVolver={() => navigate('/venta')} />;
  }
  return <ConfirmacionVentaDetalle venta={venta as VentaConfirmacion} />;
};

const ConfirmacionVentaDetalle: React.FC<{ venta: VentaConfirmacion }> = ({ venta }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { terminal } = useSession();
  const [isImprimiendo, setIsImprimiendo] = useState(false);
  const [isImprimiendoRotulo, setIsImprimiendoRotulo] = useState(false);
  const [etiquetasPDF, setEtiquetasPDF] = useState<EtiquetaPDF[]>([]);
  const [isRotuloModalOpen, setIsRotuloModalOpen] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);

  const guias = guiasDeVenta(venta);
  const identificadoresPedido = identificadoresDePedido(venta);
  const moneda: Moneda = terminal?.pais === 'MX' ? 'MXN' : 'COP';
  const money = (amount: number) => formatCurrency(amount, moneda);

  const handleImprimirTiquete = async () => {
    if (!auth.user?.access_token) return;
    setIsImprimiendo(true);
    try {
      await cargarYGenerarTiquete(venta, auth.user.access_token, terminal, moneda);
    } catch (error: unknown) {
      setErrorMensaje(mensajeDeError(error, 'No se pudo obtener la información del tiquete'));
    } finally {
      setIsImprimiendo(false);
    }
  };

  const handleImprimirRotulo = async () => {
    if (!auth.user?.access_token) return;
    setIsImprimiendoRotulo(true);
    try {
      const resultado = await cargarEtiquetasRotulo(venta.compraId, auth.user.access_token, terminal);
      if (typeof resultado === 'string') {
        setErrorMensaje(resultado);
        return;
      }
      setEtiquetasPDF(resultado);
      setIsRotuloModalOpen(true);
    } catch (error: unknown) {
      setErrorMensaje(mensajeDeError(error, 'No se pudo obtener las etiquetas'));
    } finally {
      setIsImprimiendoRotulo(false);
    }
  };

  const handleCerrarRotuloModal = () => setIsRotuloModalOpen(false);

  const esParcial = venta.estadoCompra === 'PARCIAL';
  const mensajeParcial = mensajeVentaParcial(venta.pedidos);
  const identificador = etiquetaIdentificadorPedido(identificadoresPedido, venta.compraId);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', overflowY: 'auto' }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 500, textAlign: 'center', borderRadius: 3, mx: 'auto', my: 'auto' }}>
        <CheckCircleOutlineIcon color={esParcial ? 'warning' : 'success'} sx={{ fontSize: 64, mb: 1 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold" color={esParcial ? 'warning.main' : 'success.main'}>
          {esParcial ? 'Venta Registrada con Éxito Parcial' : '¡Venta Exitosa!'}
        </Typography>
        {esParcial ? (
          <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
            {mensajeParcial}
          </Alert>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            La factura ha sido generada correctamente en SAP.
          </Typography>
        )}

        <Box sx={{ my: 3, p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, textAlign: 'left', border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {identificador.titulo}
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold">
              {identificador.valor}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Cliente</Typography>
            <Typography variant="subtitle2" fontWeight="bold" align="right">{venta.cliente.nombre}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Documento</Typography>
            <Typography variant="subtitle2" fontWeight="bold">{venta.cliente.tipoDocumento} {venta.cliente.documento}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Método de Pago</Typography>
            <Typography variant="subtitle2" fontWeight="bold">{venta.metodoPago}</Typography>
          </Box>
          {venta.tieneEnvioNacional && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {guias.length > 1 ? 'Guías' : 'Guía'}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  {guias.length > 0 ? guias.join(', ') : '—'}
                </Typography>
              </Box>
            </>
          )}
          <Divider sx={{ my: 1.5 }} />
          <DesgloseIvaMx venta={venta} moneda={moneda} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight="bold">Total Pagado</Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              {money(venta.total)}
            </Typography>
          </Box>
        </Box>

        {errorMensaje && (
          <Alert severity="error" onClose={() => setErrorMensaje(null)} sx={{ mb: 1, textAlign: 'left' }}>
            {errorMensaje}
          </Alert>
        )}

        <AccionesConfirmacion
          isImprimiendo={isImprimiendo}
          isImprimiendoRotulo={isImprimiendoRotulo}
          tieneEnvioNacional={Boolean(venta.tieneEnvioNacional)}
          onImprimirTiquete={handleImprimirTiquete}
          onImprimirRotulo={handleImprimirRotulo}
          onNuevaVenta={() => navigate('/venta')}
          onHistorial={() => navigate('/historial')}
        />
      </Paper>
      <DialogRotulos
        open={isRotuloModalOpen}
        etiquetasPDF={etiquetasPDF}
        onClose={handleCerrarRotuloModal}
      />
    </Box>
  );
};
